import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { Pagination, User } from '@interfaces';
import * as usersActions from './users.actions';
import { defaultPagination, initialState, UsersState } from './users.state';

const reducer: ActionReducer<UsersState, Action> = createReducer(
  initialState,
  on(
    usersActions.getEmployees,
    usersActions.searchForeigners,
    usersActions.getCompanyEmployees,
    usersActions.getUsers,
    (state): UsersState => ({
      ...state,
      loading: true,
      errorMessage: null,
      loadedUsersData: [],
    })
  ),

  on(
    usersActions.getUsersSuccess,
    (state, { users }): UsersState => ({
      ...state,
      loading: false,
      errorMessage: null,
      loadedUsersData: users.data,
      loadedUsersPagination: users.pagination,
    })
  ),
  on(
    usersActions.getUsersError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
      loadedUsersData: [],
      loadedUsersPagination: { ...(defaultPagination as Pagination) },
    })
  ),

  on(
    usersActions.getForeignerBasicData,
    (state): UsersState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    usersActions.getForeignerBasicDataSuccess,
    (state, { foreigner }): UsersState => ({
      ...state,
      foreignerBasicData: foreigner,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    usersActions.getForeignerBasicDataError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    usersActions.resetLoadedForeignerBasicData,
    (state): UsersState => ({
      ...state,
      foreignerBasicData: null,
    })
  ),

  on(
    usersActions.getEmployeeBasicData,
    (state): UsersState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    usersActions.getEmployeeBasicDataSuccess,
    (state, { employee }): UsersState => ({
      ...state,
      employeeBasicData: employee,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    usersActions.getEmployeeBasicDataError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    usersActions.resetLoadedEmployeeBasicData,
    (state): UsersState => ({
      ...state,
      employeeBasicData: null,
    })
  ),

  on(
    usersActions.createForeigner,
    usersActions.createEmployee,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    usersActions.createUserSuccess,
    (state, { createdUser }): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
    })
  ),
  on(
    usersActions.createUserError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    usersActions.updateEmployee,
    usersActions.updateForeigner,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    usersActions.updateUserSuccess,
    (state, { updatedUser }): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
      // if updated user is the foreigner basic data - also update it
      foreignerBasicData:
        state.foreignerBasicData?.id === updatedUser.id ? { ...updatedUser } : state.foreignerBasicData,
      // if updated user on loaded users list - update it
      loadedUsersData: state.loadedUsersData.map(user => (user.id === updatedUser.id ? updatedUser : user)),
    })
  ),
  on(
    usersActions.updateUserError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    usersActions.removeEmployee,
    usersActions.removeForeigner,
    usersActions.restoreForeigner,
    usersActions.restoreUser,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    usersActions.removeUserSuccess,
    (state, { removedUserId }): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
      // if removed user on list of loaded users - remove it
      loadedUsersData: state.loadedUsersData.filter(user => user.id !== removedUserId),
      // cached basic data would otherwise hide the deletion when the details page is reopened
      foreignerBasicData: state.foreignerBasicData?.id === removedUserId ? null : state.foreignerBasicData,
    })
  ),
  on(
    usersActions.restoreUserSuccess,
    (state, { restoredUser }): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
      // if restored user on list of loaded users - update it
      loadedUsersData: state.loadedUsersData.map(user => {
        return user.id === restoredUser.id ? (restoredUser as User) : user;
      }),
      foreignerBasicData:
        state.foreignerBasicData?.id === restoredUser.id
          ? { ...state.foreignerBasicData, deletedAt: null }
          : state.foreignerBasicData,
    })
  ),
  on(
    usersActions.removeUserError,
    usersActions.restoreUserError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    usersActions.resendForeignerActivation,
    usersActions.resendEmployeeActivation,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    usersActions.resendActivationSuccess,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
    })
  ),
  on(
    usersActions.resendActivationError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    usersActions.getEmployeeTypes,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      employeeTypes: null,
      loadingEmployeeTypes: true,
    })
  ),
  on(
    usersActions.getEmployeeTypesSuccess,
    (state, { employeeTypes }): UsersState => ({
      ...state,
      errorMessage: null,
      loadingEmployeeTypes: false,
      employeeTypes,
    })
  ),
  on(
    usersActions.getEmployeeTypesError,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loadingEmployeeTypes: false,
    })
  ),

  on(
    usersActions.selfRegister,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(
    usersActions.selfRegisterSuccess,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loading: false,
    })
  ),
  on(
    usersActions.selfRegisterError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    usersActions.getAllRoles,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      employeeTypes: null,
      loadingAllRoles: true,
    })
  ),
  on(
    usersActions.getAllRolesSuccess,
    (state, { allRoles }): UsersState => ({
      ...state,
      errorMessage: null,
      loadingAllRoles: false,
      allRoles,
    })
  ),
  on(
    usersActions.getAllRolesError,
    (state): UsersState => ({
      ...state,
      errorMessage: null,
      loadingAllRoles: false,
    })
  ),

  on(
    usersActions.reassignForeigners,
    (state): UsersState => ({
      ...state,
      reassignInProgress: true,
    })
  ),
  on(
    usersActions.reassignForeignersSuccess,
    (state): UsersState => ({
      ...state,
      reassignInProgress: false,
    })
  ),
  on(
    usersActions.reassignForeignersError,
    (state): UsersState => ({
      ...state,
      reassignInProgress: false,
    })
  ),

  on(
    usersActions.getAllRequestsDataListing,
    (state): UsersState => ({
      ...state,
      dataListingRequests: null,
      loadingDataListingRequest: true,
    })
  ),
  on(
    usersActions.getAllRequestsDataListingSuccess,
    (state, { dataListingRequests }): UsersState => ({
      ...state,
      dataListingRequests,
      loadingDataListingRequest: false,
    })
  ),
  on(
    usersActions.getAllRequestsDataListingError,
    (state): UsersState => ({
      ...state,
      dataListingRequests: null,
      loadingDataListingRequest: false,
    })
  ),

  on(
    usersActions.getDataListingRequestsOfUser,
    (state): UsersState => ({
      ...state,
      dataListingRequests: null,
      loadingDataListingRequest: true,
    })
  ),
  on(
    usersActions.getDataListingRequestsOfUserSuccess,
    (state, { dataListingRequests }): UsersState => ({
      ...state,
      dataListingRequests,
      loadingDataListingRequest: false,
    })
  ),
  on(
    usersActions.getDataListingRequestsOfUserError,
    (state): UsersState => ({
      ...state,
      dataListingRequests: null,
      loadingDataListingRequest: false,
    })
  ),

  on(
    usersActions.requestDataListingSuccess,
    (state, { dataListingRequest }): UsersState => ({
      ...state,
      dataListingRequests: [dataListingRequest, ...(state.dataListingRequests || [])],
      loadingDataListingRequest: false,
    })
  ),

  on(
    usersActions.changeDataListingRequestStatusSuccess,
    (state, { dataListingRequest }): UsersState => ({
      ...state,
      dataListingRequests: (state.dataListingRequests || []).map(request =>
        request.id === dataListingRequest.id ? { ...request, ...dataListingRequest } : request
      ),
      loadingDataListingRequest: false,
    })
  ),

  on(
    usersActions.modifyUsersTags,
    (state): UsersState => ({
      ...state,
      loadingUsersTags: true,
      errorMessage: null,
    })
  ),
  on(usersActions.modifyUsersTagsSuccess, (state, { usersTags }): UsersState => {
    const updatedUsersData = state.loadedUsersData.map(user => {
      const updated = usersTags.find(u => u.id === user.id);
      if (updated) {
        return { ...user, tags: updated.tags };
      }

      return user;
    });

    const currentForeigner = state.foreignerBasicData;
    const updatedSingle = currentForeigner && usersTags.find(u => u.id === currentForeigner.id);

    return {
      ...state,
      loadingUsersTags: false,
      errorMessage: null,
      loadedUsersData: updatedUsersData,
      foreignerBasicData: updatedSingle ? { ...currentForeigner, tags: updatedSingle.tags } : currentForeigner,
    };
  }),
  on(
    usersActions.modifyUsersTagsError,
    (state, { errorMessage }): UsersState => ({
      ...state,
      errorMessage,
      loadingUsersTags: false,
      loadedUsersData: [],
    })
  )
);

export function usersReducer(state: UsersState | undefined, action: Action): UsersState {
  return reducer(state, action);
}
